import { useState, useCallback, useRef, useEffect } from 'react';
import log from '../utilities/log';
import mediator from '../utilities/mediator';
import i18n from '../utilities/i18n';
import { DOWNLOAD_CONFIG, DOWNLOAD_MODE, DownloadStatus } from '../constants';
import { type DownloadPackageResult } from '../services/study';

export interface DownloadParams {
  downloadMode: (typeof DOWNLOAD_MODE)[keyof typeof DOWNLOAD_MODE];
  studyKey: string;
  studyName: string;
  subjectKeys?: string[];
}

export interface DownloadState {
  status: DownloadStatus;
  progress?: number;
  jobKey?: string;
  downloadUrl?: string;
  filename?: string;
  checksumDownloadUrl?: string;
  checksumFilename?: string;
  errorMessage?: string;
}

/**
 * Hook for exporting study data with progress tracking and download handling
 */
export function useStudyDownload() {
  const [downloadState, setDownloadState] = useState<DownloadState>({
    status: DownloadStatus.IDLE,
  });
  const activeRequest = useRef(false);
  const latestRequestId = useRef(0);
  const pollingController = useRef<AbortController | undefined>(undefined);

  useEffect(
    () => () => {
      latestRequestId.current += 1;
      pollingController.current?.abort();
    },
    [],
  );

  const downloadStudyData = useCallback(
    // Request refs, rather than captured state, keep this callback stable.
    async (params: DownloadParams) => {
      // Prevent concurrent downloads
      if (activeRequest.current) {
        const error = new Error(
          i18n.t('Modal_Download.error_already_in_progress'),
        );

        log.warn('Download already in progress, rejecting new request');
        throw error;
      }

      activeRequest.current = true;
      const requestId = ++latestRequestId.current;
      const controller = new AbortController();

      pollingController.current = controller;

      try {
        // Set initial status
        setDownloadState({
          status: DownloadStatus.PREPARING,
          progress: 0,
        });

        // Set processing status
        setDownloadState((prev) => ({
          ...prev,
          status: DownloadStatus.PROCESSING,
        }));

        // Build the mediator request
        let topic: string;
        let requestData: Record<string, any>;

        switch (params.downloadMode) {
          case DOWNLOAD_MODE.STUDY:
            topic = 'study:downloadStudyData';
            requestData = { studyKey: params.studyKey };
            break;
          case DOWNLOAD_MODE.SUBJECTS:
            topic = 'study:downloadSelectedSubjectData';
            requestData = { subjectKeys: params.subjectKeys };
            break;
          default:
            throw new Error(`Invalid download mode: ${params.downloadMode}`);
        }

        const mediatorRequest = {
          ...requestData,
          signal: controller.signal,
          onJobCreated: (jobKey: string) => {
            if (requestId !== latestRequestId.current) {
              return;
            }

            setDownloadState((prev) => ({ ...prev, jobKey }));
          },
          onAcquiring: () => {
            if (requestId !== latestRequestId.current) {
              return;
            }

            setDownloadState((prev) => ({
              ...prev,
              status: DownloadStatus.ACQUIRING,
              progress: 100,
            }));
          },
          onProgress: (progressPercent: number) => {
            if (requestId !== latestRequestId.current) {
              return;
            }

            setDownloadState((prev) => ({
              ...prev,
              progress: progressPercent,
            }));
          },
          onReconnecting: (reconnecting: boolean) => {
            if (requestId !== latestRequestId.current) {
              return;
            }

            setDownloadState((prev) => ({
              ...prev,
              status: reconnecting
                ? DownloadStatus.RECONNECTING
                : DownloadStatus.PROCESSING,
            }));
          },
        };

        // Use the service for actual download
        const downloadResult = await mediator.request<DownloadPackageResult>(
          topic,
          mediatorRequest,
        );

        if (requestId !== latestRequestId.current) {
          return;
        }

        // Keep the existing bearer-authenticated Blob download until native
        // downloads and the required deployment policy are released together.
        const { blob, checksumSha256 } = downloadResult;
        const url = window.URL.createObjectURL(blob);
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const filename = `${params.studyName}_${timestamp}${DOWNLOAD_CONFIG.FILE_EXTENSION}`;
        let checksumDownloadUrl: string | undefined;
        let checksumFilename: string | undefined;

        if (checksumSha256) {
          const checksumText = `${checksumSha256}  ${filename}\n`;
          const checksumBlob = new Blob([checksumText], {
            type: 'text/plain;charset=utf-8',
          });

          checksumDownloadUrl = window.URL.createObjectURL(checksumBlob);
          checksumFilename = `${filename}.sha256`;
        }

        // Clean up download URLs and update state
        setDownloadState((prev) => {
          if (prev.downloadUrl && prev.downloadUrl !== url) {
            window.URL.revokeObjectURL(prev.downloadUrl);
          }

          if (
            prev.checksumDownloadUrl &&
            prev.checksumDownloadUrl !== checksumDownloadUrl
          ) {
            window.URL.revokeObjectURL(prev.checksumDownloadUrl);
          }

          return {
            ...prev,
            status: DownloadStatus.COMPLETED,
            downloadUrl: url,
            filename,
            checksumDownloadUrl,
            checksumFilename,
            errorMessage: undefined,
          };
        });

        return {
          success: true,
          filename,
          downloadUrl: url,
          checksumDownloadUrl,
          checksumFilename,
        };
      } catch (error) {
        if (requestId !== latestRequestId.current) {
          return;
        }

        log.error('download failed:', error);

        setDownloadState((prev) => ({
          ...prev,
          status: DownloadStatus.FAILED,
          errorMessage:
            error instanceof Error
              ? error.message
              : i18n.t('Modal_Download.error_generic_failed'),
        }));
        throw error;
      } finally {
        if (requestId === latestRequestId.current) {
          activeRequest.current = false;
        }
      }
    },
    [],
  );

  // Clean up download URLs and reset state
  const resetDownload = useCallback(() => {
    // Stop browser polling only; closing this modal does not cancel generation.
    latestRequestId.current += 1;
    pollingController.current?.abort();
    activeRequest.current = false;

    if (downloadState.downloadUrl) {
      window.URL.revokeObjectURL(downloadState.downloadUrl);
    }

    if (downloadState.checksumDownloadUrl) {
      window.URL.revokeObjectURL(downloadState.checksumDownloadUrl);
    }

    setDownloadState({
      status: DownloadStatus.IDLE,
    });
  }, [downloadState.downloadUrl, downloadState.checksumDownloadUrl]);

  return {
    downloadStudyData,
    resetDownload,
    isPending:
      downloadState.status === DownloadStatus.PREPARING ||
      downloadState.status === DownloadStatus.PROCESSING ||
      downloadState.status === DownloadStatus.ACQUIRING ||
      downloadState.status === DownloadStatus.RECONNECTING,
    downloadState,
  };
}

export default useStudyDownload;
